// Deal Calculator Functions for Rentlytix
// Ported from Deal-Analyzer

// Helper to get value or default (properly handles 0)
const getVal = (value, defaultValue) => {
  return (value !== undefined && value !== null && value !== '') ? value : defaultValue;
};

export function calculateMonthlyPI(principal, annualRate, years) {
  if (principal <= 0 || years <= 0) return 0;
  const monthlyRate = (annualRate / 100) / 12;
  const nPayments = years * 12;
  if (monthlyRate === 0) return principal / nPayments;
  return principal * (monthlyRate * Math.pow(1 + monthlyRate, nPayments)) / (Math.pow(1 + monthlyRate, nPayments) - 1);
}

// BRRRR Calculator
export function calculateBRRRRMetrics(deal) {
  const prop = deal.property_info || {};
  const acq = deal.acquisition || {};
  const rehab = deal.rehab || {};
  const hold = deal.holding_rent || {};
  const refi = deal.refinance || {};
  
  const purchasePrice = acq.purchase_price > 0 ? acq.purchase_price : getVal(prop.listing_price, 0);
  const units = prop.units > 0 ? prop.units : 1;
  
  // Rehab
  const totalRehabCost = getVal(rehab.rehab_budget, 0) * (1 + getVal(rehab.contingency_percent, 10) / 100);
  
  // Acquisition
  const closingCosts = (purchasePrice * getVal(acq.closing_costs_percent, 0) / 100) + getVal(acq.closing_costs_fixed, 0);
  
  let loanAmount, downPayment, pointsFees, initialMonthlyPI;
  const includeRehab = acq.short_term_io_include_rehab !== false;
  
  if (acq.short_term_io_enabled) {
    const totalProjectCost = includeRehab ? (purchasePrice + totalRehabCost) : purchasePrice;
    loanAmount = totalProjectCost * getVal(acq.short_term_io_ltc_percent, 90) / 100;
    downPayment = totalProjectCost - loanAmount;
    pointsFees = (loanAmount * getVal(acq.short_term_io_points, 0) / 100) + getVal(acq.lender_fees, 0);
    initialMonthlyPI = loanAmount * (getVal(acq.short_term_io_rate, 0) / 100) / 12;
  } else {
    // Calculate loan amount based on down payment (100% - down payment %)
    const downPaymentPercent = getVal(acq.down_payment_percent, 20);
    const loanPercent = 100 - downPaymentPercent;
    loanAmount = purchasePrice * loanPercent / 100;
    downPayment = purchasePrice * downPaymentPercent / 100;
    pointsFees = (loanAmount * getVal(acq.points_percent, 0) / 100) + getVal(acq.lender_fees, 0);
    initialMonthlyPI = calculateMonthlyPI(loanAmount, getVal(acq.interest_rate, 0), getVal(acq.amortization_years, 30));
  }
  
  const totalCashAtClosing = downPayment + closingCosts + pointsFees;
  
  // Holding costs during rehab
  let monthlyInterest;
  if (acq.short_term_io_enabled) {
    monthlyInterest = loanAmount * (getVal(acq.short_term_io_rate, 0) / 100) / 12;
  } else if (rehab.holding_payment_type === "interest_only") {
    monthlyInterest = loanAmount * (getVal(acq.interest_rate, 0) / 100) / 12;
  } else {
    monthlyInterest = initialMonthlyPI;
  }
  
  const monthlyHolding = monthlyInterest + 
    getVal(rehab.holding_taxes_monthly, 0) + 
    getVal(rehab.holding_insurance_monthly, 0) + 
    getVal(rehab.holding_utilities_monthly, 0) +
    getVal(rehab.holding_other_monthly, 0);
  const holdingCostsTotal = monthlyHolding * getVal(rehab.rehab_timeline_months, 3);
  
  let totalCashBeforeRefi;
  if (acq.short_term_io_enabled && includeRehab) {
    totalCashBeforeRefi = totalCashAtClosing + holdingCostsTotal;
  } else {
    totalCashBeforeRefi = totalCashAtClosing + totalRehabCost + holdingCostsTotal;
  }
  
  // Income & Expenses
  const grossScheduledRent = getVal(hold.market_rent_monthly, 0) * 12;
  const vacancyLoss = grossScheduledRent * getVal(hold.vacancy_percent, 8) / 100;
  const effectiveGrossIncome = grossScheduledRent - vacancyLoss + (getVal(hold.other_income_monthly, 0) * 12);
  
  const annualTaxes = hold.property_taxes_annual > 0 ? hold.property_taxes_annual : getVal(prop.property_taxes_annual, 0);
  const annualInsurance = getVal(hold.insurance_annual, 0);
  const annualUtilities = getVal(hold.utilities_monthly, 0) * 12;
  const annualRepairs = grossScheduledRent * getVal(hold.repairs_percent, 5) / 100;
  const annualCapex = grossScheduledRent * getVal(hold.capex_percent, 5) / 100;
  const annualPM = grossScheduledRent * getVal(hold.property_management_percent, 8) / 100;
  const annualLandscaping = (hold.landscaping_monthly || 0) * 12;
  const annualHOA = (hold.hoa_monthly || 0) * 12;
  const annualMisc = (hold.misc_monthly || 0) * 12;
  
  const totalOperatingExpenses = annualTaxes + annualInsurance + annualUtilities + 
    annualRepairs + annualCapex + annualPM + annualLandscaping + annualHOA + annualMisc;
  const noi = effectiveGrossIncome - totalOperatingExpenses;
  
  // Refinance
  const arv = refi.arv > 0 ? refi.arv : purchasePrice;
  const newLoanAmount = arv * (refi.refi_ltv_percent || 75) / 100;
  const newMonthlyPI = calculateMonthlyPI(newLoanAmount, refi.new_interest_rate || 6.5, refi.new_amortization_years || 30);
  const refiCosts = newLoanAmount * (refi.refi_closing_costs_percent || 2) / 100;
  
  const existingPayoff = loanAmount;
  const cashOutFromRefi = newLoanAmount - existingPayoff - refiCosts;
  const cashLeftInDeal = totalCashBeforeRefi - cashOutFromRefi;
  
  // BRRRR Metrics
  const equityCreated = arv - newLoanAmount;
  let totalReturnOnCash = 0;
  if (totalCashBeforeRefi > 0) {
    totalReturnOnCash = (cashOutFromRefi / totalCashBeforeRefi) * 100;
  }
  
  const annualDebtService = newMonthlyPI * 12;
  const annualCashFlow = noi - annualDebtService;
  const monthlyCashFlow = annualCashFlow / 12;
  
  let cashOnCashReturn = 0;
  if (cashLeftInDeal > 0) {
    cashOnCashReturn = (annualCashFlow / cashLeftInDeal) * 100;
  } else if (cashLeftInDeal <= 0 && annualCashFlow > 0) {
    cashOnCashReturn = Infinity;
  }
  
  const capRateArv = arv > 0 ? (noi / arv) * 100 : 0;
  const capRatePurchase = purchasePrice > 0 ? (noi / purchasePrice) * 100 : 0;
  const dscr = annualDebtService > 0 ? noi / annualDebtService : 0;
  
  const totalFixedCosts = totalOperatingExpenses + annualDebtService;
  const breakEvenOccupancy = grossScheduledRent > 0 ? (totalFixedCosts / grossScheduledRent) * 100 : 0;
  
  const rentPerUnit = units > 0 ? (hold.market_rent_monthly || 0) / units : 0;
  const expensePerUnit = units > 0 ? (totalOperatingExpenses / 12) / units : 0;
  const cashFlowPerUnit = units > 0 ? monthlyCashFlow / units : 0;
  
  return {
    closing_costs: closingCosts,
    loan_amount: loanAmount,
    down_payment: downPayment,
    points_fees: pointsFees,
    total_cash_at_closing: totalCashAtClosing,
    initial_monthly_pi: initialMonthlyPI,
    total_rehab_cost: totalRehabCost,
    holding_costs_total: holdingCostsTotal,
    total_cash_before_refi: totalCashBeforeRefi,
    gross_scheduled_rent: grossScheduledRent,
    effective_gross_income: effectiveGrossIncome,
    total_operating_expenses: totalOperatingExpenses,
    noi: noi,
    new_loan_amount: newLoanAmount,
    new_monthly_pi: newMonthlyPI,
    cash_out_from_refi: cashOutFromRefi,
    cash_left_in_deal: cashLeftInDeal,
    equity_created: equityCreated,
    total_return_on_cash: totalReturnOnCash,
    monthly_cash_flow: monthlyCashFlow,
    annual_cash_flow: annualCashFlow,
    cash_on_cash_return: cashOnCashReturn,
    cap_rate_arv: capRateArv,
    cap_rate_purchase: capRatePurchase,
    dscr: dscr,
    break_even_occupancy: breakEvenOccupancy,
    rent_per_unit: rentPerUnit,
    expense_per_unit: expensePerUnit,
    cash_flow_per_unit: cashFlowPerUnit,
  };
}

// Fix & Flip Calculator
export function calculateFlipMetrics(deal) {
  const prop = deal.property_info || {};
  const acq = deal.acquisition || {};
  const rehab = deal.rehab || {};
  const sale = deal.sale || {};
  
  const purchasePrice = acq.purchase_price > 0 ? acq.purchase_price : getVal(prop.listing_price, 0);
  
  // Rehab
  const totalRehabCost = getVal(rehab.rehab_budget, 0) * (1 + getVal(rehab.contingency_percent, 10) / 100);
  
  // Acquisition
  const closingCosts = (purchasePrice * getVal(acq.closing_costs_percent, 0) / 100) + getVal(acq.closing_costs_fixed, 0);
  
  let loanAmount, downPayment, pointsFees, initialMonthlyPayment;
  const includeRehab = acq.short_term_io_include_rehab !== false;
  
  if (acq.short_term_io_enabled) {
    const totalProjectCost = includeRehab ? (purchasePrice + totalRehabCost) : purchasePrice;
    loanAmount = totalProjectCost * getVal(acq.short_term_io_ltc_percent, 90) / 100;
    downPayment = totalProjectCost - loanAmount;
    pointsFees = (loanAmount * getVal(acq.short_term_io_points, 2) / 100) + getVal(acq.lender_fees, 0);
    initialMonthlyPayment = loanAmount * (getVal(acq.short_term_io_rate, 12) / 100) / 12;
  } else {
    // Calculate loan amount based on down payment (100% - down payment %)
    const downPaymentPercent = getVal(acq.down_payment_percent, 20);
    const loanPercent = 100 - downPaymentPercent;
    loanAmount = purchasePrice * loanPercent / 100;
    downPayment = purchasePrice * downPaymentPercent / 100;
    pointsFees = (loanAmount * getVal(acq.points_percent, 0) / 100) + getVal(acq.lender_fees, 0);
    initialMonthlyPayment = calculateMonthlyPI(loanAmount, getVal(acq.interest_rate, 0), getVal(acq.amortization_years, 30));
  }
  
  const totalCashAtClosing = downPayment + closingCosts + pointsFees;
  
  // Holding costs
  const holdingMonths = getVal(sale.holding_time_months, 0) > 0 ? sale.holding_time_months : getVal(rehab.rehab_timeline_months, 3);
  
  let monthlyInterest;
  if (acq.short_term_io_enabled) {
    monthlyInterest = loanAmount * (getVal(acq.short_term_io_rate, 12) / 100) / 12;
  } else if (rehab.holding_payment_type === "interest_only") {
    monthlyInterest = loanAmount * (getVal(acq.interest_rate, 0) / 100) / 12;
  } else {
    monthlyInterest = initialMonthlyPayment;
  }
  
  const monthlyHolding = monthlyInterest + 
    getVal(rehab.holding_taxes_monthly, 0) + 
    getVal(rehab.holding_insurance_monthly, 0) + 
    getVal(rehab.holding_utilities_monthly, 0) +
    getVal(rehab.holding_other_monthly, 0);
  const holdingCostsTotal = monthlyHolding * holdingMonths;
  
  // Total investment
  let totalCashInvested;
  if (acq.short_term_io_enabled && includeRehab) {
    totalCashInvested = totalCashAtClosing + holdingCostsTotal;
  } else {
    totalCashInvested = totalCashAtClosing + totalRehabCost + holdingCostsTotal;
  }
  
  const totalProjectCost = purchasePrice + totalRehabCost + closingCosts + holdingCostsTotal + pointsFees;
  
  // Sale
  const grossSalePrice = getVal(sale.sale_price, 0) > 0 ? sale.sale_price : getVal(sale.arv, 0);
  const sellingCosts = (grossSalePrice * getVal(sale.selling_costs_percent, 8) / 100) + getVal(sale.selling_costs_fixed, 0);
  const netSaleProceeds = grossSalePrice - sellingCosts;
  const loanPayoff = loanAmount;
  
  // Profit
  const grossProfit = netSaleProceeds - purchasePrice - totalRehabCost;
  const netProfit = netSaleProceeds - loanPayoff - totalCashInvested;
  
  let roiPercent = 0;
  let annualizedRoi = 0;
  if (totalCashInvested > 0) {
    roiPercent = (netProfit / totalCashInvested) * 100;
    if (holdingMonths > 0) {
      annualizedRoi = roiPercent * (12 / holdingMonths);
    }
  }
  
  const profitMargin = grossSalePrice > 0 ? (netProfit / grossSalePrice) * 100 : 0;
  
  return {
    closing_costs: closingCosts,
    loan_amount: loanAmount,
    down_payment: downPayment,
    points_fees: pointsFees,
    total_cash_at_closing: totalCashAtClosing,
    initial_monthly_payment: initialMonthlyPayment,
    total_rehab_cost: totalRehabCost,
    holding_costs_total: holdingCostsTotal,
    total_cash_invested: totalCashInvested,
    total_project_cost: totalProjectCost,
    gross_sale_price: grossSalePrice,
    selling_costs: sellingCosts,
    net_sale_proceeds: netSaleProceeds,
    loan_payoff: loanPayoff,
    gross_profit: grossProfit,
    net_profit: netProfit,
    roi_percent: roiPercent,
    annualized_roi: annualizedRoi,
    profit_margin: profitMargin,
  };
}

// Buy and Hold Calculator
export function calculateBuyHoldMetrics(deal) {
  const prop = deal.property_info || {};
  const acq = deal.acquisition || {};
  const rehab = deal.rehab || {};
  const hold = deal.holding_rent || {};
  
  const purchasePrice = acq.purchase_price > 0 ? acq.purchase_price : getVal(prop.listing_price, 0);
  const units = prop.units > 0 ? prop.units : 1;
  
  // Rehab
  const totalRehabCost = getVal(rehab.rehab_budget, 0) * (1 + getVal(rehab.contingency_percent, 10) / 100);
  
  // Acquisition
  const closingCosts = (purchasePrice * getVal(acq.closing_costs_percent, 0) / 100) + getVal(acq.closing_costs_fixed, 0);
  
  let loanAmount, downPayment, pointsFees, monthlyPI;
  const includeRehab = acq.short_term_io_include_rehab !== false;
  
  if (acq.short_term_io_enabled) {
    const totalProjectCost = includeRehab ? (purchasePrice + totalRehabCost) : purchasePrice;
    loanAmount = totalProjectCost * getVal(acq.short_term_io_ltc_percent, 90) / 100;
    downPayment = totalProjectCost - loanAmount;
    pointsFees = (loanAmount * getVal(acq.short_term_io_points, 2) / 100) + getVal(acq.lender_fees, 0);
    monthlyPI = loanAmount * (getVal(acq.short_term_io_rate, 12) / 100) / 12;
  } else {
    // Calculate loan amount based on down payment (100% - down payment %)
    const downPaymentPercent = getVal(acq.down_payment_percent, 20);
    const loanPercent = 100 - downPaymentPercent;
    loanAmount = purchasePrice * loanPercent / 100;
    downPayment = purchasePrice * downPaymentPercent / 100;
    pointsFees = (loanAmount * getVal(acq.points_percent, 0) / 100) + getVal(acq.lender_fees, 0);
    monthlyPI = calculateMonthlyPI(loanAmount, getVal(acq.interest_rate, 0), getVal(acq.amortization_years, 30));
  }
  
  const totalCashAtClosing = downPayment + closingCosts + pointsFees;
  
  // Total Investment
  let totalCashInvested;
  if (acq.short_term_io_enabled && includeRehab) {
    totalCashInvested = totalCashAtClosing;
  } else {
    totalCashInvested = totalCashAtClosing + totalRehabCost;
  }
  
  // Income & Expenses
  const grossScheduledRent = getVal(hold.market_rent_monthly, 0) * 12;
  const vacancyLoss = grossScheduledRent * getVal(hold.vacancy_percent, 8) / 100;
  const effectiveGrossIncome = grossScheduledRent - vacancyLoss + (getVal(hold.other_income_monthly, 0) * 12);
  
  const annualTaxes = hold.property_taxes_annual > 0 ? hold.property_taxes_annual : getVal(prop.property_taxes_annual, 0);
  const annualInsurance = getVal(hold.insurance_annual, 0);
  const annualUtilities = getVal(hold.utilities_monthly, 0) * 12;
  const annualRepairs = grossScheduledRent * getVal(hold.repairs_percent, 5) / 100;
  const annualCapex = grossScheduledRent * getVal(hold.capex_percent, 5) / 100;
  const annualPM = grossScheduledRent * getVal(hold.property_management_percent, 8) / 100;
  const annualLandscaping = getVal(hold.landscaping_monthly, 0) * 12;
  const annualHOA = getVal(hold.hoa_monthly, 0) * 12;
  const annualMisc = getVal(hold.misc_monthly, 0) * 12;
  
  const totalOperatingExpenses = annualTaxes + annualInsurance + annualUtilities + 
    annualRepairs + annualCapex + annualPM + annualLandscaping + annualHOA + annualMisc;
  const noi = effectiveGrossIncome - totalOperatingExpenses;
  
  // Cash Flow
  const annualDebtService = monthlyPI * 12;
  const annualCashFlow = noi - annualDebtService;
  const monthlyCashFlow = annualCashFlow / 12;
  
  // Returns
  let cashOnCashReturn = 0;
  let roiPercent = 0;
  if (totalCashInvested > 0) {
    cashOnCashReturn = (annualCashFlow / totalCashInvested) * 100;
    roiPercent = cashOnCashReturn;
  }
  
  // Property value
  const totalPropertyValue = purchasePrice + totalRehabCost;
  const capRate = totalPropertyValue > 0 ? (noi / totalPropertyValue) * 100 : 0;
  const equity = totalPropertyValue - loanAmount;
  
  const dscr = annualDebtService > 0 ? noi / annualDebtService : 0;
  
  // Break-even occupancy
  const totalFixedCosts = totalOperatingExpenses + annualDebtService;
  const breakEvenOccupancy = grossScheduledRent > 0 ? (totalFixedCosts / grossScheduledRent) * 100 : 0;
  
  // Per unit metrics
  const rentPerUnit = units > 0 ? getVal(hold.market_rent_monthly, 0) / units : 0;
  const expensePerUnit = units > 0 ? (totalOperatingExpenses / 12) / units : 0;
  const cashFlowPerUnit = units > 0 ? monthlyCashFlow / units : 0;
  
  return {
    closing_costs: closingCosts,
    loan_amount: loanAmount,
    down_payment: downPayment,
    points_fees: pointsFees,
    total_cash_at_closing: totalCashAtClosing,
    monthly_pi: monthlyPI,
    total_rehab_cost: totalRehabCost,
    total_cash_invested: totalCashInvested,
    gross_scheduled_rent: grossScheduledRent,
    effective_gross_income: effectiveGrossIncome,
    total_operating_expenses: totalOperatingExpenses,
    noi: noi,
    monthly_cash_flow: monthlyCashFlow,
    annual_cash_flow: annualCashFlow,
    cash_on_cash_return: cashOnCashReturn,
    roi_percent: roiPercent,
    cap_rate: capRate,
    dscr: dscr,
    break_even_occupancy: breakEvenOccupancy,
    total_property_value: totalPropertyValue,
    equity: equity,
    rent_per_unit: rentPerUnit,
    expense_per_unit: expensePerUnit,
    cash_flow_per_unit: cashFlowPerUnit,
  };
}

// Formatting helpers
export function formatCurrency(value) {
  if (value === undefined || value === null || isNaN(value)) return "$0";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export function formatPercent(value) {
  if (value === undefined || value === null || isNaN(value)) return "0.00%";
  if (!isFinite(value)) return "∞";
  return `${value.toFixed(2)}%`;
}

export function formatNumber(value, decimals = 2) {
  if (value === undefined || value === null || isNaN(value)) return "0";
  if (!isFinite(value)) return "∞";
  return value.toFixed(decimals);
}

// Subject-To + Seller Finance Calculator
export function calculateSubjectToMetrics(deal) {
  const prop = deal.property_info || {};
  const income = deal.income || {};
  const subjectTo = deal.subject_to || {};
  const sellerFinance = deal.seller_finance || {};
  const expenses = deal.expenses || {};
  const reserves = deal.reserves || {};
  const cashInvested = deal.cash_invested || {};

  const purchasePrice = getVal(subjectTo.purchase_price, 0);

  // === INCOME ===
  const monthlyRent = getVal(income.monthly_rent, 0);
  const otherIncome = getVal(income.other_income_monthly, 0);
  const grossScheduledRent = monthlyRent + otherIncome;

  // === SUBJECT-TO LOAN ===
  const existingLoanBalance = getVal(subjectTo.existing_loan_balance, 0);
  const existingInterestRate = getVal(subjectTo.existing_interest_rate, 0);
  const existingRemainingYears = getVal(subjectTo.existing_remaining_years, 25);
  
  // Calculate subject-to payment if not provided
  let subjectToPayment = getVal(subjectTo.existing_monthly_payment, 0);
  if (subjectToPayment === 0 && existingLoanBalance > 0) {
    subjectToPayment = calculateMonthlyPI(existingLoanBalance, existingInterestRate, existingRemainingYears);
  }

  // === SELLER FINANCE ===
  const downPayment = getVal(sellerFinance.down_payment, 0);
  const closingCosts = (purchasePrice * getVal(subjectTo.closing_costs_percent, 3) / 100) + getVal(subjectTo.closing_costs_fixed, 0);
  
  // Auto-calculate seller finance amount or use override
  let sellerFinanceAmount = getVal(sellerFinance.loan_amount, 0);
  if (sellerFinanceAmount === 0 || sellerFinance.auto_calculate !== false) {
    sellerFinanceAmount = Math.max(0, purchasePrice - existingLoanBalance - downPayment);
  }
  
  const sellerInterestRate = getVal(sellerFinance.interest_rate, 6);
  const sellerAmortYears = getVal(sellerFinance.amortization_years, 30);
  
  // Calculate seller finance payment
  let sellerFinancePayment = getVal(sellerFinance.monthly_payment_override, 0);
  if (sellerFinancePayment === 0 && sellerFinanceAmount > 0) {
    sellerFinancePayment = calculateMonthlyPI(sellerFinanceAmount, sellerInterestRate, sellerAmortYears);
  }

  // Total debt service
  const totalMonthlyDebtService = subjectToPayment + sellerFinancePayment;
  const annualDebtService = totalMonthlyDebtService * 12;

  // === OPERATING EXPENSES ===
  const propertyTaxesMonthly = getVal(expenses.property_taxes_annual, 0) / 12 || getVal(expenses.property_taxes_monthly, 0);
  const insuranceMonthly = getVal(expenses.insurance_monthly, 0);
  const utilitiesMonthly = getVal(expenses.utilities_monthly, 0);
  const hoaMonthly = getVal(expenses.hoa_monthly, 0);
  const otherFixedMonthly = getVal(expenses.other_fixed_monthly, 0);

  // === PERCENTAGE-BASED RESERVES ===
  const vacancyRate = getVal(reserves.vacancy_percent, 5) / 100;
  const vacancyReserve = grossScheduledRent * vacancyRate;

  // Repairs - either percentage or fixed
  let repairsMonthly;
  if (reserves.repairs_use_percent !== false) {
    repairsMonthly = grossScheduledRent * getVal(reserves.repairs_percent, 8) / 100;
  } else {
    repairsMonthly = getVal(reserves.repairs_fixed, 0);
  }

  // CapEx - either percentage or fixed
  let capexMonthly;
  if (reserves.capex_use_percent !== false) {
    capexMonthly = grossScheduledRent * getVal(reserves.capex_percent, 8) / 100;
  } else {
    capexMonthly = getVal(reserves.capex_fixed, 0);
  }

  // Property Management - either percentage or fixed
  let pmMonthly;
  if (reserves.pm_use_percent !== false) {
    pmMonthly = grossScheduledRent * getVal(reserves.property_management_percent, 10) / 100;
  } else {
    pmMonthly = getVal(reserves.pm_fixed, 0);
  }

  // Total operating expenses
  const totalFixedExpenses = propertyTaxesMonthly + insuranceMonthly + utilitiesMonthly + hoaMonthly + otherFixedMonthly;
  const totalVariableExpenses = repairsMonthly + capexMonthly + pmMonthly;
  const totalOperatingExpenses = totalFixedExpenses + totalVariableExpenses;

  // === INCOME CALCULATIONS ===
  const effectiveGrossIncome = grossScheduledRent - vacancyReserve;
  const netOperatingIncome = effectiveGrossIncome - totalOperatingExpenses;
  const annualNOI = netOperatingIncome * 12;

  // === CASH FLOW ===
  const monthlyCashFlow = netOperatingIncome - totalMonthlyDebtService;
  const annualCashFlow = monthlyCashFlow * 12;

  // === CASH INVESTED ===
  const initialRepairs = getVal(cashInvested.initial_repairs, 0);
  const otherUpfrontCosts = getVal(cashInvested.other_upfront, 0);
  const totalCashInvested = downPayment + closingCosts + initialRepairs + otherUpfrontCosts;

  // === RETURNS ===
  let cashOnCashReturn = 0;
  if (totalCashInvested > 0) {
    cashOnCashReturn = (annualCashFlow / totalCashInvested) * 100;
  }

  // DSCR
  const dscr = annualDebtService > 0 ? annualNOI / annualDebtService : 0;

  // Deal quality indicator
  const targetCoCReturn = getVal(reserves.target_coc_return, 10);
  const isDeal = cashOnCashReturn >= targetCoCReturn && monthlyCashFlow > 0;

  // Equity position
  const totalEquity = purchasePrice - existingLoanBalance - sellerFinanceAmount;

  return {
    // Income
    gross_scheduled_rent: grossScheduledRent,
    vacancy_reserve: vacancyReserve,
    effective_gross_income: effectiveGrossIncome,
    monthly_rent: monthlyRent,
    other_income: otherIncome,

    // Subject-To Loan
    existing_loan_balance: existingLoanBalance,
    subject_to_payment: subjectToPayment,

    // Seller Finance
    seller_finance_amount: sellerFinanceAmount,
    seller_finance_payment: sellerFinancePayment,
    down_payment: downPayment,
    closing_costs: closingCosts,

    // Debt Service
    total_monthly_debt_service: totalMonthlyDebtService,
    annual_debt_service: annualDebtService,

    // Operating Expenses
    property_taxes_monthly: propertyTaxesMonthly,
    insurance_monthly: insuranceMonthly,
    utilities_monthly: utilitiesMonthly,
    hoa_monthly: hoaMonthly,
    other_fixed_monthly: otherFixedMonthly,
    repairs_monthly: repairsMonthly,
    capex_monthly: capexMonthly,
    pm_monthly: pmMonthly,
    total_fixed_expenses: totalFixedExpenses,
    total_variable_expenses: totalVariableExpenses,
    total_operating_expenses: totalOperatingExpenses,

    // NOI and Cash Flow
    noi: netOperatingIncome,
    annual_noi: annualNOI,
    monthly_cash_flow: monthlyCashFlow,
    annual_cash_flow: annualCashFlow,

    // Investment
    initial_repairs: initialRepairs,
    other_upfront: otherUpfrontCosts,
    total_cash_invested: totalCashInvested,

    // Returns
    cash_on_cash_return: cashOnCashReturn,
    dscr: dscr,
    is_deal: isDeal,
    total_equity: totalEquity,
    purchase_price: purchasePrice,
  };
}
